// Generates an A4 invoice PDF for a demo payable on the fly. Used by the
// mock axios adapter when `bw-invoices/downloadAttachment` is hit.
//
// Relies on @cantoo/pdf-lib (already in the repo deps, browser-friendly,
// ~250KB gzipped — only loaded when a PDF is requested, never on initial page
// load).

import { DEMO_COMPANY_BY_ID } from "./companies.ts";
import type { DemoPayable } from "./payables.ts";

// Matches the VAT the payables mock books for the customer's jurisdiction.
const VAT_BY_JURISDICTION = { AT: 0.2, CZ: 0.21, PL: 0.23, DE: 0.19, GB: 0.2 };

function vatRateFor(p: DemoPayable): number {
  const company = DEMO_COMPANY_BY_ID[p.companyId];
  return company ? VAT_BY_JURISDICTION[company.jurisdiction] : 0.21;
}

type LineItem = {
  description: string;
  quantity: number;
  unitPrice: number;
};

// Hand-rolled line items derived from supplier-name heuristics. Real-looking
// for a few common SME spend categories.
function lineItemsFor(p: DemoPayable): LineItem[] {
  const name = p.supplierName.toLowerCase();
  const totalNet = p.amount / (1 + vatRateFor(p));

  if (name.includes("vexa") || name.includes("halofield") || name.includes("beampoint") || name.includes("quillstream") || name.includes("pixelpath") || name.includes("tetra")) {
    return [
      { description: `${p.supplierName} subscription — May 2026`, quantity: 1, unitPrice: totalNet * 0.6 },
      { description: "Additional seats (4 users)", quantity: 4, unitPrice: (totalNet * 0.4) / 4 },
    ];
  }
  if (name.includes("brickline")) {
    return [
      { description: "Office rent — Friedrichstraße 90, Berlin", quantity: 1, unitPrice: totalNet * 0.92 },
      { description: "Building service fees", quantity: 1, unitPrice: totalNet * 0.08 },
    ];
  }
  if (name.includes("aurea")) {
    return [
      { description: "Electricity — May 2026 (5,420 kWh)", quantity: 5420, unitPrice: totalNet / 5420 },
    ];
  }
  if (name.includes("mintway") || name.includes("telecom")) {
    return [
      { description: "Corporate mobile tariff — 8 lines", quantity: 8, unitPrice: totalNet / 8 },
    ];
  }
  if (name.includes("aerlite") || name.includes("travel")) {
    return [
      { description: "Berlin — Vienna return flight (Anna Schmidt)", quantity: 2, unitPrice: totalNet * 0.46 },
      { description: "Hotel — 2 nights", quantity: 2, unitPrice: (totalNet * 0.54) / 2 },
    ];
  }
  if (name.includes("vanguard") || name.includes("northpoint")) {
    return [
      { description: "Monthly closing + filings", quantity: 1, unitPrice: totalNet * 0.7 },
      { description: "Advisory hours (4h)", quantity: 4, unitPrice: (totalNet * 0.3) / 4 },
    ];
  }
  if (name.includes("plumb") || name.includes("audit")) {
    return [
      { description: "Annual audit — fiscal year 2025", quantity: 1, unitPrice: totalNet },
    ];
  }
  if (name.includes("tilly") || name.includes("sparrow") || name.includes("halo")) {
    return [
      { description: "Brand design sprint — phase 1", quantity: 1, unitPrice: totalNet * 0.55 },
      { description: "Production + delivery", quantity: 1, unitPrice: totalNet * 0.45 },
    ];
  }
  if (name.includes("coastward") || name.includes("aerial")) {
    return [
      { description: "Outbound shipments (42 parcels)", quantity: 42, unitPrice: totalNet / 42 },
    ];
  }
  if (name.includes("solune") || name.includes("office")) {
    return [
      { description: "Office supplies — paper + kitchen", quantity: 1, unitPrice: totalNet * 0.6 },
      { description: "Coffee subscription", quantity: 1, unitPrice: totalNet * 0.4 },
    ];
  }
  if (name.includes("brightcircle") || name.includes("insurance")) {
    return [
      { description: "D&O insurance premium — quarter", quantity: 1, unitPrice: totalNet },
    ];
  }
  if (name.includes("externí") || name.includes("bárta")) {
    return [
      { description: "Externí účetnictví — duben 2026", quantity: 1, unitPrice: totalNet },
    ];
  }
  return [{ description: `Services rendered — ${p.invoiceNumber}`, quantity: 1, unitPrice: totalNet }];
}

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Generates the PDF Blob. Lazy-imports pdf-lib so the dependency isn't pulled
// into the dashboard chunk.
export async function generateInvoicePdf(p: DemoPayable): Promise<Blob> {
  const { PDFDocument, StandardFonts, rgb } = await import("@cantoo/pdf-lib");

  const doc = await PDFDocument.create();
  const helv = await doc.embedFont(StandardFonts.Helvetica);
  const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const page = doc.addPage([595, 842]); // A4
  const W = page.getWidth();
  const margin = 50;

  const darkSlate = rgb(0.07, 0.13, 0.2);
  const muted = rgb(0.39, 0.47, 0.56);
  const border = rgb(0.85, 0.88, 0.92);
  const brand = rgb(0.0, 0.39, 0.27); // Contoso dark green

  let y = 800;

  // Header — supplier branding (left) + INVOICE (right).
  page.drawText(p.supplierName, { x: margin, y, size: 16, font: helvBold, color: darkSlate });
  y -= 16;
  page.drawText(`VAT: ${p.supplierVatNumber}`, { x: margin, y, size: 9, font: helv, color: muted });
  y -= 30;

  page.drawText("INVOICE", { x: W - margin - 100, y: 800, size: 22, font: helvBold, color: brand });

  // Invoice meta box (right).
  const metaX = W - margin - 200;
  let metaY = 770;
  const drawMeta = (label: string, value: string) => {
    page.drawText(label, { x: metaX, y: metaY, size: 8, font: helv, color: muted });
    page.drawText(value, { x: metaX + 80, y: metaY, size: 9, font: helvBold, color: darkSlate });
    metaY -= 16;
  };
  drawMeta("Invoice no.", p.invoiceNumber);
  drawMeta("Issue date", new Date(p.issuedDate).toLocaleDateString("en-IE"));
  drawMeta("Due date", new Date(p.dueDate).toLocaleDateString("en-IE"));
  drawMeta("Currency", p.currency);
  drawMeta("Status", p.status);

  // Bill-to block (left).
  y = 720;
  page.drawText("BILL TO", { x: margin, y, size: 8, font: helv, color: muted });
  y -= 12;
  const customer = DEMO_COMPANY_BY_ID[p.companyId];
  page.drawText(customer?.name ?? "Contoso Group", { x: margin, y, size: 11, font: helvBold, color: darkSlate });
  y -= 14;
  for (const line of [
    customer?.street ?? "",
    `${customer?.zip ?? ""} ${customer?.city ?? ""}`.trim(),
    `VAT: ${customer?.vatNumber ?? ""}`,
  ]) {
    page.drawText(line, { x: margin, y, size: 9, font: helv, color: darkSlate });
    y -= 12;
  }

  // Line items table.
  y = 600;
  page.drawLine({ start: { x: margin, y }, end: { x: W - margin, y }, thickness: 1, color: border });
  y -= 16;
  const colDesc = margin;
  const colQty = W - margin - 220;
  const colUnit = W - margin - 130;
  const colTotal = W - margin - 60;

  page.drawText("Description", { x: colDesc, y, size: 8, font: helvBold, color: muted });
  page.drawText("Qty", { x: colQty, y, size: 8, font: helvBold, color: muted });
  page.drawText("Unit price", { x: colUnit, y, size: 8, font: helvBold, color: muted });
  page.drawText("Total", { x: colTotal, y, size: 8, font: helvBold, color: muted });
  y -= 6;
  page.drawLine({ start: { x: margin, y }, end: { x: W - margin, y }, thickness: 1, color: border });

  const items = lineItemsFor(p);
  for (const item of items) {
    y -= 22;
    page.drawText(item.description.slice(0, 60), { x: colDesc, y, size: 10, font: helv, color: darkSlate });
    page.drawText(String(item.quantity), { x: colQty, y, size: 10, font: helv, color: darkSlate });
    page.drawText(formatAmount(item.unitPrice, p.currency), { x: colUnit, y, size: 10, font: helv, color: darkSlate });
    const lineTotal = item.quantity * item.unitPrice;
    page.drawText(formatAmount(lineTotal, p.currency), { x: colTotal, y, size: 10, font: helvBold, color: darkSlate });
  }

  y -= 32;
  page.drawLine({ start: { x: margin, y }, end: { x: W - margin, y }, thickness: 1, color: border });

  // Totals block.
  const subtotal = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const vatRate = vatRateFor(p);
  const vat = subtotal * vatRate;
  const total = subtotal + vat;
  const drawTotalLine = (label: string, value: string, bold = false) => {
    y -= 18;
    page.drawText(label, { x: colUnit - 40, y, size: 10, font: bold ? helvBold : helv, color: bold ? darkSlate : muted });
    page.drawText(value, { x: colTotal, y, size: 10, font: bold ? helvBold : helv, color: darkSlate });
  };
  drawTotalLine("Subtotal", formatAmount(subtotal, p.currency));
  drawTotalLine(`VAT (${Math.round(vatRate * 100)}%)`, formatAmount(vat, p.currency));
  drawTotalLine("Total due", formatAmount(total, p.currency), true);

  // Payment instructions footer.
  y -= 70;
  page.drawText("PAYMENT", { x: margin, y, size: 8, font: helv, color: muted });
  y -= 14;
  page.drawText(`Reference: ${p.invoiceNumber}`, { x: margin, y, size: 9, font: helv, color: darkSlate });
  y -= 12;
  page.drawText(`Due by ${new Date(p.dueDate).toLocaleDateString("en-IE")}`, { x: margin, y, size: 9, font: helv, color: darkSlate });

  // Note (optional).
  if (p.notes) {
    y -= 30;
    page.drawText("Notes", { x: margin, y, size: 8, font: helv, color: muted });
    y -= 12;
    page.drawText(p.notes.slice(0, 100), { x: margin, y, size: 9, font: helv, color: darkSlate });
  }

  // Footer band.
  page.drawLine({ start: { x: margin, y: 60 }, end: { x: W - margin, y: 60 }, thickness: 1, color: border });
  page.drawText(`${p.supplierName} · Generated for Contoso Group demo`, {
    x: margin,
    y: 46,
    size: 8,
    font: helv,
    color: muted,
  });

  const bytes = await doc.save();
  return new Blob([bytes as BlobPart], { type: "application/pdf" });
}
